// @flow

const Logging = {};

/** @enum {number} */
Logging.LOG_LEVEL = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

type LOG_LEVEL = 0 | 1 | 2 | 3;

let _defaultLogLevel: LOG_LEVEL = Logging.LOG_LEVEL.DEBUG;

type LogFunction = (...args: Array<mixed>) => void;

export type LoggerType = {
  _getLogLevel: () => LOG_LEVEL,
  debug: LogFunction,
  info: LogFunction,
  warn: LogFunction,
  error: LogFunction,
  setLogLevel: (logLevel: LOG_LEVEL) => void,
};

/**
 * DON'T USE THIS DIRECTLY USE Logging.getLogger() INSTEAD.
 *
 * A logger that allows printing to the console and sending messages to the
 * server. A message will print to the console iff the
 * log message's level is equal to or greater than the level of the logger.
 * All log errors will be sent to the server.
 *
 * @param {string} name The name of the logger, all messages this log emits
 *     to the console will start with this name.
 * @constructor
 */
const Logger = function(name: string) {
  /** @type {string} */
  this.name = name;

  /** @type {?Logging.LOG_LEVEL} */
  this.logLevel = null;
};

/**
 * @private
 */
Logger.prototype._getLogLevel = function() {
  if (this.logLevel === null) {
    return _defaultLogLevel;
  } else {
    return this.logLevel;
  }
};

/**
 * @param {...*} args
 */
Logger.prototype.debug = function(...args: Array<mixed>) {
  this._handleLog(Logging.LOG_LEVEL.DEBUG, args);
};

/**
 * @param {...*} args
 */
Logger.prototype.info = function(...args: Array<mixed>) {
  this._handleLog(Logging.LOG_LEVEL.INFO, args);
};

/**
 * @param {...*} args
 */
Logger.prototype.warn = function(...args: Array<mixed>) {
  this._handleLog(Logging.LOG_LEVEL.WARN, args);
};

/**
 * @param {...*} args
 */
Logger.prototype.error = function(...args: Array<mixed>) {
  this._handleLog(Logging.LOG_LEVEL.ERROR, args);
};

/**
 * @param {Logging.LOG_LEVEL} logLevel
 * @param {!Array} messages
 */
Logger.prototype._handleLog = function(logLevel: LOG_LEVEL, messages: Array<mixed>): void {
  if (logLevel < this._getLogLevel()) {
    return;
  }

  Logging.handleLogMessage({
    loggerName: this.name,
    logLevel,
    messages,
  });
};

/**
 * @param {Logging.LOG_LEVEL} logLevel
 */
Logger.prototype.setLogLevel = function(logLevel: LOG_LEVEL) {
  this.logLevel = logLevel;
};

/**
 * List of all of the created loggers.
 *
 * @type {Array.<!Logger>}
 */
let allLoggers = [];

/**
 * Map of log name to logger.
 *
 * @type {Object.<string, !Logger>}
 */
let nameToLogger = {};

/* eslint-disable no-unused-expressions */
/**
 * @typedef {{loggerName:string, logLevel:Logging.LOG_LEVEL, messages:!Array}}
 */
Logging.LogMessage;
/* eslint-enable */

export type LogMessage = {
  loggerName: string,
  logLevel: LOG_LEVEL,
  messages: Array<mixed>,
};

/**
 * @typedef {!Array.<function(!Logging.LogMessage)>}
 */
const handlers = [];

/** @param {function(!Logging.LogMessage)} callback */
Logging.addHandler = function(callback: (message: LogMessage) => void) {
  handlers.push(callback);
};

/** @param {!Logging.LogMessage} logMessage */
Logging.handleLogMessage = function(logMessage: LogMessage) {
  handlers.forEach((handler) => handler(logMessage));
};

/**
 * Get a logger with the given name.
 *
 * @param {!string} name The name of the logger.
 * @return {!Logger} Either a new or shared logger, depending on
 *     whether a logger with that name has been created before.
 */
Logging.getLogger = function(name: string): LoggerType {
  let logger = nameToLogger[name];
  if (!logger) {
    logger = new Logger(name);
    nameToLogger[name] = logger;
    allLoggers.push(logger);
  }
  return logger;
};

/**
 * Gets all of the loggers that have been created.
 *
 * @return {Array.<Logger>}
 */
Logging.getAll = function() {
  return allLoggers;
};

/**
 * Gets all of the loggers that have been created.
 *
 * @param {Logging.LOG_LEVEL} logLevel
 */
Logging.setDefaultLogLevel = function(logLevel: LOG_LEVEL) {
  _defaultLogLevel = logLevel;
};

/**
 * Gets the logger by name if it exists.
 *
 * @param {!string} name The name of the logger.
 * @return {?Logger}
 */
Logging.getByName = function(name: string) {
  return nameToLogger[name];
};

Logging.clear = function() {
  allLoggers = [];
  nameToLogger = {};
};

export default Logging;
export { Logger };
